import { useId, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { AdminDrawer } from "@admin/_components/AdminDrawer";
import { Alert } from "@common/Alert";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { ROUTES } from "@routing/routes";
import type { CreateClientResponse } from "@models/organization";
import {
    createClientRefusal,
    type CreateClientRefusal,
} from "../_helpers/createClient";
import {
    createClientFormSchema,
    firstLocationFormSchema,
    toCreateClientRequest,
    toFirstLocationFormValues,
    type CreateClientFormValues,
    type FirstLocationFormValues,
} from "../_helpers/createClientForm";
import { CreateClientOutcome } from "./CreateClientOutcome";
import { FirstLocationFields } from "./FirstLocationFields";
import { FormField, INPUT_CLASS } from "./FormField";
import { OrganizationFormFields } from "./OrganizationFormFields";

interface CreateClientDrawerProps {
    onClose: () => void;
    // A 201: the client exists, so the page re-reads its list. The drawer
    // stays open on the outcome.
    onCreated: () => void;
}

// Empty strings, not undefined, so a blank field reads "… is required".
const EMPTY_CLIENT: Partial<CreateClientFormValues> = {
    name: "",
    legalBusinessName: "",
    taxId: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
};

// C3 (API PR 419): a platform admin creates a client: the organization, its
// contact (the organization's one user, who is sent the signup invitation)
// and its first location. The certificate the admin uploads approves that
// location in the same step (C3/C4 follow-up). A refusal keeps the drawer open
// with the admin's entries and says why; a 201 shows what the API did.
export const CreateClientDrawer = ({
    onClose,
    onCreated,
}: CreateClientDrawerProps) => {
    const { createClient } = useOrganizationApi();
    const runGuarded = useSubmitGuard();
    const formId = useId();
    const taxIdInputId = useId();
    const certificateInputId = useId();

    const [isCreating, setIsCreating] = useState(false);
    const [refusal, setRefusal] = useState<CreateClientRefusal | null>(null);
    // The tax id the duplicate warning named: "Create anyway" confirms that
    // one only, not one typed after the warning.
    const [warnedTaxId, setWarnedTaxId] = useState<string | null>(null);
    // The first location has a name and address of its own (else the
    // organization's).
    const [ownLocation, setOwnLocation] = useState(false);
    const [created, setCreated] = useState<{
        response: CreateClientResponse | undefined;
    } | null>(null);

    const methods = useForm<CreateClientFormValues>({
        resolver: zodResolver(createClientFormSchema),
        defaultValues: EMPTY_CLIENT,
    });
    const {
        register,
        formState: { errors },
    } = methods;
    const locationMethods = useForm<FirstLocationFormValues>({
        resolver: zodResolver(firstLocationFormSchema),
    });
    // Read while rendering, so react-hook-form tracks it.
    const isLocationEdited = locationMethods.formState.isDirty;

    // The first location's own fields, checked: null when a rule refused
    // them (each says so at its field).
    const readOwnLocation = () =>
        new Promise<FirstLocationFormValues | null>((resolve) => {
            void locationMethods.handleSubmit(resolve, () => resolve(null))();
        });

    const create = (values: CreateClientFormValues, confirmRequested: boolean) =>
        runGuarded(async () => {
            const location = ownLocation ? await readOwnLocation() : null;
            if (ownLocation && !location) {
                return;
            }

            setRefusal(null);
            setIsCreating(true);

            try {
                const response = await createClient(
                    toCreateClientRequest(
                        values,
                        location,
                        confirmRequested && values.taxId === warnedTaxId,
                    ),
                );
                setCreated({ response });
                onCreated();
            } catch (error) {
                const refused = createClientRefusal(error);
                setRefusal(refused);
                setWarnedTaxId(
                    refused.kind === "tax_id_in_use" ? values.taxId : null,
                );
            } finally {
                setIsCreating(false);
            }
        });

    const submit = (confirmRequested: boolean) =>
        methods.handleSubmit(
            (values) => create(values, confirmRequested),
            // The first location's errors show beside the organization's.
            () => {
                if (ownLocation) {
                    void readOwnLocation();
                }
            },
        );

    // Starts from what the organization holds now (Lead call: the first
    // location defaults to the organization's name and address), until the
    // admin edits it: turned off and on again, their entries stay (PR 440 L5).
    const changeOwnLocation = (own: boolean) => {
        if (own && !isLocationEdited) {
            locationMethods.reset(toFirstLocationFormValues(methods.getValues()));
        }
        setOwnLocation(own);
    };

    if (created) {
        return (
            <AdminDrawer
                title="New Client"
                onClose={onClose}
                footer={
                    <Button
                        color={ComponentColor.Primary}
                        onClick={onClose}
                    >
                        Done
                    </Button>
                }
            >
                <CreateClientOutcome response={created.response} />
            </AdminDrawer>
        );
    }

    return (
        <AdminDrawer
            title="New Client"
            onClose={onClose}
            isProcessing={isCreating}
            footer={
                <div className="flex items-center gap-4">
                    {/* Outside the form, so tied to it by `form`. A native
                        button: Common Button does not forward `form`. */}
                    <button
                        type="submit"
                        form={formId}
                        disabled={isCreating}
                        className="btn btn-primary"
                    >
                        {isCreating && (
                            <span className="loading loading-spinner" />
                        )}
                        {isCreating ? "Creating…" : "Create Client"}
                    </button>
                    {/* text-neutral: a ghost button on a light surface is
                        otherwise white on white (see the UI CLAUDE.md). */}
                    <Button
                        color={ComponentColor.Ghost}
                        className="text-neutral"
                        disabled={isCreating}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                </div>
            }
        >
            {refusal && (
                <Alert
                    color={
                        refusal.kind === "tax_id_in_use"
                            ? ComponentColor.Warning
                            : ComponentColor.Error
                    }
                    className="mb-4"
                >
                    <p>{refusal.message}</p>
                    {refusal.kind === "email_in_use" &&
                        refusal.existingOrganizationId && (
                        <Link
                            to={ROUTES.ADMIN_CLIENTS_DETAIL(
                                refusal.existingOrganizationId,
                            )}
                            className="link mt-2 inline-block font-semibold"
                        >
                            Open that client
                        </Link>
                    )}
                    {refusal.kind === "tax_id_in_use" && (
                        <>
                            {refusal.existingOrganizations.length > 0 && (
                                <ul className="mt-2 list-disc pl-5">
                                    {refusal.existingOrganizations.map(
                                        (client) => (
                                            <li key={client.id}>
                                                <Link
                                                    to={ROUTES.ADMIN_CLIENTS_DETAIL(
                                                        client.id,
                                                    )}
                                                    className="link font-semibold"
                                                >
                                                    {client.name}
                                                </Link>
                                            </li>
                                        ),
                                    )}
                                </ul>
                            )}
                            <Button
                                size={ComponentSize.SM}
                                outline
                                className="mt-2"
                                disabled={isCreating}
                                onClick={() => void submit(true)()}
                            >
                                Create anyway
                            </Button>
                        </>
                    )}
                </Alert>
            )}
            <FormProvider {...methods}>
                <form id={formId} noValidate onSubmit={submit(false)}>
                    <fieldset
                        disabled={isCreating}
                        className="flex flex-col gap-4"
                    >
                        <OrganizationFormFields
                            taxIdField={
                                <FormField
                                    id={taxIdInputId}
                                    label="Tax ID"
                                    error={errors.taxId?.message}
                                >
                                    <input
                                        id={taxIdInputId}
                                        type="text"
                                        className={INPUT_CLASS}
                                        {...register("taxId")}
                                    />
                                </FormField>
                            }
                        />
                        <fieldset className="flex flex-col gap-4">
                            <legend className="mb-2 font-bold">
                                First Location
                            </legend>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm"
                                    checked={!ownLocation}
                                    onChange={(event) =>
                                        changeOwnLocation(!event.target.checked)
                                    }
                                />
                                Same name and address as the organization
                            </label>
                            {ownLocation && (
                                <FormProvider {...locationMethods}>
                                    <FirstLocationFields />
                                </FormProvider>
                            )}
                            <FormField
                                id={certificateInputId}
                                label="Resale Certificate"
                                error={errors.resaleCertificate?.message}
                            >
                                <input
                                    id={certificateInputId}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="file-input file-input-bordered w-full bg-white text-neutral"
                                    {...register("resaleCertificate")}
                                />
                                <p className="mt-1 text-xs text-neutral/60">
                                    PDF, JPG, or PNG, up to 10 MB.
                                </p>
                            </FormField>
                            <p className="text-sm">
                                Uploading the certificate approves this
                                location. It then waits for payment until the
                                customer adds a card after following the
                                invitation email.
                            </p>
                        </fieldset>
                    </fieldset>
                </form>
            </FormProvider>
        </AdminDrawer>
    );
};
